<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div>
    <p>Available everywhere in our app using <code>&lt;Button /&gt;</code></p>
    <p>
      props: <code>{{ allProps.join(', ') }}</code>
    </p>
    <h4>Default</h4>
    <div class="flex items-center gap-2">
      <CButton>default</CButton>
      <CButton disabled> default [disabled] </CButton>
      <CButton :loading="true"> default [loading] </CButton>
      <CButton :loadingProgress="70"> default [loading] </CButton>
      <CButton href="#"> default [a] </CButton>
      <CButton icon="volunteer_activism"> icon </CButton>
      <CButton icon="volunteer_activism" :iconBefore="true">
        icon-before
      </CButton>
    </div>

    <div v-for="variant in colors" :key="'xx-' + variant">
      <h4>{{ variant }}</h4>
      <p>
        <code>&lt;Button color="{{ variant }}" /&gt;</code>
      </p>
      <div class="flex items-center gap-2">
        <CButton :color="variant">
          {{ variant }}
        </CButton>
        <CButton :color="variant" disabled> {{ variant }} [disabled] </CButton>
        <CButton :color="variant" :loading="true">
          {{ variant }} [loading]
        </CButton>
        <CButton :color="variant" :loadingProgress="30">
          {{ variant }} [loading]
        </CButton>
        <CButton :color="variant" href="#"> {{ variant }} [a] </CButton>
        <CButton :color="variant" icon="volunteer_activism"> icon </CButton>
        <CButton :color="variant" icon="volunteer_activism" :iconBefore="true">
          icon-before
        </CButton>
      </div>
    </div>
    <h2>Router</h2>
    <p>
      Allows to use links to internal routes.
      <code>&lt;Button to="pageName" /&gt;</code>
    </p>

    <div class="flex items-center gap-2">
      <CButton to="playground"> router-link </CButton>
      <CButton to="playground" tooltip="It's a tooltip!"> tooltip </CButton>
      <CButton to="playground" disabled> disabled </CButton>
      <CButton to="playground" color="signal"> customclass </CButton>
      <CButton to="playground" icon="volunteer_activism"> icon </CButton>
      <CButton to="playground" icon="volunteer_activism" />
      <CButton to="playground" size="sml"> small </CButton>
    </div>
    <h2>Button widths</h2>
    <p>
      Buttons have different available widths:
      <code>{{ sizes.join(', ') }}</code>
    </p>
    <div :style="{ display: 'grid', gap: '0.5rem' }">
      <div v-for="variant in sizes" :key="'size-' + variant">
        <CButton :width="variant">
          {{ variant }}
        </CButton>
      </div>
    </div>
    <h2>sml</h2>
    <p>
      <code>&lt;Button size="sml" /&gt;</code>
    </p>
    <div class="flex items-center gap-2">
      <CButton size="sml"> [sml] </CButton>
      <CButton
        v-for="variant in colors"
        :key="'btnsml-' + variant"
        :color="variant"
        size="sml"
      >
        {{ variant }} [sml]
      </CButton>
      <CButton size="sml" icon="volunteer_activism"> icon </CButton>
      <CButton size="sml" icon="volunteer_activism" />
    </div>
    <h2>icons</h2>
    <p>
      <code>&lt;Button icon="volunteer_activism" /&gt;</code>
    </p>
    <div class="miniStack">
      <div class="flex items-center gap-2">
        <div>
          <CButton icon="volunteer_activism" />
        </div>
        <div v-for="variant in colors" :key="'icon-' + variant">
          <CButton
            :color="variant"
            icon="volunteer_activism"
            :tooltip="variant"
          />
        </div>
        <div>
          <CButton size="sml" icon="volunteer_activism" tooltip="sml" />
        </div>
      </div>
      <div class="flex items-center gap-2">
        <div v-for="variant in icons" :key="'icon2-' + variant">
          <CButton :icon="variant" />
        </div>
        <div v-for="variant in icons" :key="'icon3-' + variant">
          <CButton :icon="variant" size="sml" />
        </div>
      </div>
    </div>
    <h2>menu</h2>
    <p>
      <code>&lt;Button variant="menu" /&gt;</code>
    </p>

    <div class="flex items-center gap-2">
      <CButton variant="menu"> menu </CButton>
      <CButton variant="menu" disabled> default [disabled] </CButton>
      <CButton variant="menu" :loading="true"> default [loading] </CButton>
      <CButton variant="menu" href="#"> default [a] </CButton>
      <CButton variant="menu" icon="volunteer_activism"> icon </CButton>
      <CButton variant="menu" icon="volunteer_activism" :iconBefore="true">
        icon-before
      </CButton>
    </div>
    <h2>loading</h2>
    <p>
      <code>&lt;Button :loading="true" /&gt;</code> and
      <code>&lt;Button :loadingProgress="30" /&gt;</code>
    </p>

    <div class="flex items-center gap-2">
      <CButton :loading="true"> indefinite loading </CButton>
      <CButton v-for="p in [10, 30, 50, 70, 100]" :key="p" :loadingProgress="p">
        progress {{ p }}
      </CButton>
      <CButton :loading="true" size="sml"> indefinite loading </CButton>
    </div>

    <h2>buttons group</h2>

    <ButtonGroup>
      <CButton icon="volunteer_activism" />
      <CButton icon="volunteer_activism" />
      <CButton icon="volunteer_activism" color="active" />
      <CButton icon="volunteer_activism" />
      <CButton icon="volunteer_activism" />
    </ButtonGroup>

    <h2>flex row</h2>
    <p>
      Inside <code>flex items-center gap-2</code>, buttons stretch to fill the
      row.
    </p>
    <div class="miniStack">
      <div class="flex items-center gap-2">
        <CButton icon="volunteer_activism"> very long text </CButton>
      </div>
      <div class="flex items-center gap-2">
        <CButton icon="volunteer_activism"> short text </CButton>
        <CButton icon="volunteer_activism"> very long text </CButton>
      </div>
      <div class="flex items-center gap-2">
        <CButton icon="volunteer_activism"> text </CButton>
        <CButton icon="volunteer_activism"> short text </CButton>
        <CButton icon="volunteer_activism"> very long text </CButton>
      </div>
    </div>
    <h2>fluid-width</h2>
    <p>
      Use <code>&lt;Button width="fluid-width" /&gt;</code> so each button is
      only as wide as its label.
    </p>
    <div class="flex items-center gap-2">
      <CButton width="fluid-width" icon="volunteer_activism"> text </CButton>
      <CButton width="fluid-width" icon="volunteer_activism">
        short text
      </CButton>
      <CButton width="fluid-width" icon="volunteer_activism">
        very long text
      </CButton>
    </div>
    <h2>Raw-class modifiers</h2>
    <p>
      Applied as <code>class="..."</code> rather than a typed prop today —
      candidates for formalizing as props.
    </p>
    <div class="miniStack">
      <div class="flex items-center gap-2">
        <CButton class="transparent">transparent</CButton>
        <CButton size="lg">lg</CButton>
        <CButton class="tab-btn">tab-btn</CButton>
        <CButton class="green-dashed-border">green-dashed-border</CButton>
      </div>
      <div class="flex items-center gap-2">
        <Lock :locked="true" />
        <Lock :locked="false" />
      </div>
    </div>

    <h2>Buttons onClick</h2>
    <p>
      Use a function that returns a promise to have the button show a loader
      until it's resolved
      <code>&lt;Button @click="promise" /&gt;</code>
    </p>
    <div class="flex items-center gap-2">
      <CButton width="fluid-width" icon="volunteer_activism" @click="promise">
        text
      </CButton>
    </div>

    <section class="miniStack">
      <h2>Showcase</h2>
      <div class="flex items-center gap-2">
        <BaseCard>
          <h2>Buttons in a flex row</h2>
          <p>
            {{ longText }}
          </p>
          <div class="flex items-center gap-2">
            <CButton icon="not_interested" color="green"> Accept </CButton>
            <CButton icon="not_interested" color="alert"> Decline </CButton>
          </div>
        </BaseCard>
      </div>
      <div class="miniStack">
        <BaseCard>
          <h2>Buttons in a flex row</h2>
          <p>
            {{ longText }}
          </p>
          <div class="flex items-center gap-2">
            <CButton icon="not_interested" color="green"> Accept </CButton>
            <CButton icon="not_interested" color="alert"> Decline </CButton>
          </div>
        </BaseCard>
        <BaseCard>
          <h2>Buttons aligned right</h2>
          <p>
            {{ longText }}
          </p>
          <div class="flex items-center gap-2">
            <CButton
              icon="not_interested"
              color="green"
              width="fluid-width"
              class="ml-auto"
            >
              Accept
            </CButton>
            <CButton icon="not_interested" color="alert" width="fluid-width">
              Decline
            </CButton>
          </div>
        </BaseCard>
      </div>

      <BaseCard class="flex items-center gap-2">
        <h4 style="flex: 1">Quote 1</h4>
        <div class="flex items-center gap-2">
          <CButton color="white" width="fluid-width" icon="check">
            Ready
          </CButton>
          <CButton color="white" width="fluid-width" icon="visibility">
            View
          </CButton>
        </div>
      </BaseCard>
    </section>
  </div>
</template>

<script setup>
import CButton, { sizes, colors } from '@/components/common/CButton.vue';
import ButtonGroup from '@/components/common/ButtonGroup.vue';
import Lock from '@/components/elements/Lock.vue';
const icons = [
  'forest',
  'cabin',
  'bakery_dining',
  'subway',
  'edit_attributes',
  'route',
  'navigation',
  'celebration',
];

const allProps = Object.keys(CButton.props);

const longText = `Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
            eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim
            ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut
            aliquip ex ea commodo consequat. Duis aute irure dolor in
            reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla
            pariatur. Excepteur sint occaecat cupidatat non proident, sunt in
            culpa qui officia deserunt mollit anim id est laborum.`;

const promise = () => new Promise((res) => setTimeout(res, 1500));
</script>
